/**
 * Script Evolution Tracker — Shows how a script's scores have changed
 * across multiple Greenlight analysis versions.
 */
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  TrendingUp, TrendingDown, ArrowUp, ArrowDown, History, Minus,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SUB_SCORE_META = {
  writing_craft:          { label: 'Writing Craft',          color: '#8B5CF6' },
  character_arc:          { label: 'Character Arc',          color: '#EC4899' },
  emotional_architecture: { label: 'Emotional Architecture', color: '#14B8A6' },
  narrative_logic:        { label: 'Narrative Logic',        color: '#F97316' },
  commercial_viability:   { label: 'Commercial Viability',   color: '#06B6D4' },
  audience_breadth:       { label: 'Audience Breadth',       color: '#A78BFA' },
  producibility:          { label: 'Producibility',          color: '#34D399' },
  originality:            { label: 'Originality',            color: '#FB923C' },
};

const TIER_THRESHOLDS = [
  { y: 90, label: 'Gold',   color: '#FFD700' },
  { y: 80, label: 'Silver', color: '#A0AEC0' },
  { y: 70, label: 'Bronze', color: '#CD7F32' },
];

const BADGE_COLORS = {
  gold:   { bg: 'bg-amber-500/20',  text: 'text-amber-400',  border: 'border-amber-500/40' },
  silver: { bg: 'bg-zinc-400/20',   text: 'text-zinc-300',   border: 'border-zinc-400/40' },
  bronze: { bg: 'bg-orange-600/20', text: 'text-orange-400', border: 'border-orange-500/40' },
  null:   { bg: 'bg-zinc-700/20',   text: 'text-[var(--text-muted)]', border: 'border-zinc-600/40' },
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function buildTimeline(history, currentAnalysis) {
  const items = [...(history || [])];

  // Append current analysis if not already in history
  if (currentAnalysis && !items.find((h) => h.id === currentAnalysis.id)) {
    items.push(currentAnalysis);
  }

  // Sort by created_at
  items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  return items.map((item, idx) => ({
    ...item,
    versionLabel: item.title || `Draft ${idx + 1}`,
    versionIndex: idx + 1,
  }));
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function deltaIcon(delta) {
  if (delta > 0) return <ArrowUp className="w-3.5 h-3.5 text-emerald-400 inline" />;
  if (delta < 0) return <ArrowDown className="w-3.5 h-3.5 text-red-400 inline" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-500 inline" />;
}

function deltaClass(delta) {
  if (delta > 0) return 'text-emerald-400';
  if (delta < 0) return 'text-red-400';
  return 'text-zinc-500';
}

function scoreColor(score) {
  if (score >= 80) return '#FFD700';
  if (score >= 60) return '#4ECDC4';
  if (score >= 40) return '#FF8C42';
  return '#DC143C';
}

/* ------------------------------------------------------------------ */
/*  Custom Tooltip                                                     */
/* ------------------------------------------------------------------ */

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-xl"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
        color: 'var(--text-primary)',
      }}
    >
      <p className="font-semibold mb-1.5 text-[var(--text-primary)]">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 py-0.5">
          <span
            className="w-2 h-2 rounded-full inline-block"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-[var(--text-muted)]">{entry.name}:</span>
          <span className="font-medium" style={{ color: entry.color }}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section 1: Score Progression Chart                                 */
/* ------------------------------------------------------------------ */

function ScoreChart({ timeline, visibleLines }) {
  const chartData = timeline.map((v) => {
    const point = { name: v.versionLabel, greenlight_score: v.greenlight_score };
    if (v.sub_scores) {
      Object.entries(v.sub_scores).forEach(([key, val]) => {
        point[key] = val;
      });
    }
    return point;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="rounded-xl border p-4"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-4">
        Score Progression
      </h4>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--border-subtle)' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--border-subtle)' }}
              tickLine={false}
              width={35}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Tier threshold reference lines */}
            {TIER_THRESHOLDS.map((t) => (
              <ReferenceLine
                key={t.label}
                y={t.y}
                stroke={t.color}
                strokeDasharray="6 4"
                strokeOpacity={0.35}
                label={{
                  value: t.label,
                  position: 'right',
                  fill: t.color,
                  fontSize: 10,
                  opacity: 0.6,
                }}
              />
            ))}

            {/* Main greenlight score line */}
            <Line
              type="monotone"
              dataKey="greenlight_score"
              name="Greenlight Score"
              stroke="#FFD700"
              strokeWidth={3}
              dot={{ r: 5, fill: '#FFD700', strokeWidth: 0 }}
              activeDot={{ r: 7, fill: '#FFD700', stroke: '#fff', strokeWidth: 2 }}
            />

            {/* Sub-score lines */}
            {Object.entries(SUB_SCORE_META).map(([key, meta]) =>
              visibleLines[key] ? (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={meta.label}
                  stroke={meta.color}
                  strokeWidth={1.5}
                  strokeOpacity={0.8}
                  dot={{ r: 3, fill: meta.color, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: meta.color, stroke: '#fff', strokeWidth: 1 }}
                />
              ) : null
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Toggle buttons for sub-scores */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {Object.entries(SUB_SCORE_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => visibleLines._toggle(key)}
            className={`
              px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all duration-200
              ${visibleLines[key]
                ? 'border-opacity-60 bg-opacity-20'
                : 'border-zinc-700 bg-transparent text-zinc-500 hover:text-zinc-400'
              }
            `}
            style={
              visibleLines[key]
                ? { borderColor: meta.color, backgroundColor: `${meta.color}20`, color: meta.color }
                : undefined
            }
          >
            {meta.label}
          </button>
        ))}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section 2: Delta Table                                             */
/* ------------------------------------------------------------------ */

function DeltaTable({ prev, curr }) {
  const metrics = useMemo(() => {
    const rows = [
      { key: 'greenlight_score', label: 'Greenlight Score', isPrimary: true },
      ...Object.entries(SUB_SCORE_META).map(([key, meta]) => ({
        key, label: meta.label, isPrimary: false,
      })),
    ];

    return rows.map((row) => {
      const prevVal = row.isPrimary ? prev.greenlight_score : prev.sub_scores?.[row.key] ?? null;
      const currVal = row.isPrimary ? curr.greenlight_score : curr.sub_scores?.[row.key] ?? null;
      const delta = prevVal != null && currVal != null ? currVal - prevVal : null;
      return { ...row, prevVal, currVal, delta };
    });
  }, [prev, curr]);

  const validMetrics = metrics.filter((m) => m.delta !== null);
  const biggestImprovement = validMetrics.reduce(
    (best, m) => (m.delta > (best?.delta ?? -Infinity) ? m : best), null
  );
  const biggestRegression = validMetrics.reduce(
    (worst, m) => (m.delta < (worst?.delta ?? Infinity) ? m : worst), null
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <div className="px-4 py-3 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">
          Version Comparison
        </h4>
        <span className="text-[11px] text-[var(--text-muted)]">
          {prev.versionLabel} → {curr.versionLabel}
        </span>
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
        {metrics.map((m) => {
          if (m.delta === null) return null;
          const isBestUp = biggestImprovement && m.key === biggestImprovement.key && m.delta > 0;
          const isWorstDown = biggestRegression && m.key === biggestRegression.key && m.delta < 0;

          return (
            <div
              key={m.key}
              className={`
                grid grid-cols-4 items-center px-4 py-2 text-xs
                ${isBestUp ? 'bg-emerald-500/5' : ''}
                ${isWorstDown ? 'bg-red-500/5' : ''}
              `}
              style={{ borderColor: 'var(--border-subtle)' }}
            >
              <span className={`font-medium ${m.isPrimary ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}>
                {m.label}
                {isBestUp && (
                  <span className="ml-1.5 text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
                    BEST
                  </span>
                )}
                {isWorstDown && (
                  <span className="ml-1.5 text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full">
                    NEEDS WORK
                  </span>
                )}
              </span>
              <span className="text-center text-[var(--text-muted)]">{m.prevVal}</span>
              <span className="text-center text-[var(--text-primary)] font-medium">{m.currVal}</span>
              <span className={`text-center font-semibold ${deltaClass(m.delta)}`}>
                {deltaIcon(m.delta)}
                <span className="ml-1">
                  {m.delta > 0 ? `+${m.delta}` : m.delta === 0 ? '—' : m.delta}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section 3: Version Cards                                           */
/* ------------------------------------------------------------------ */

function VersionCards({ timeline }) {
  const latestId = timeline[timeline.length - 1]?.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
        Version History
      </h4>

      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-zinc-700">
        {timeline.map((v) => {
          const isLatest = v.id === latestId;
          const color = scoreColor(v.greenlight_score);
          const badgeStyle = BADGE_COLORS[v.badge?.tier] || BADGE_COLORS[null];

          return (
            <motion.div
              key={v.id}
              whileHover={{ scale: 1.03 }}
              className={`
                flex-shrink-0 w-44 rounded-xl border p-3 transition-colors
                ${isLatest ? 'border-amber-500/60 bg-amber-500/5' : ''}
              `}
              style={{
                backgroundColor: isLatest ? undefined : 'var(--bg-elevated)',
                borderColor: isLatest ? undefined : 'var(--border-subtle)',
              }}
            >
              {/* Title & date */}
              <p className="text-xs font-semibold text-[var(--text-primary)] truncate">
                {v.versionLabel}
              </p>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                {formatDate(v.created_at)}
              </p>

              {/* Score circle */}
              <div className="flex justify-center my-3">
                <div className="relative w-14 h-14">
                  <svg width={56} height={56} className="-rotate-90">
                    <circle
                      cx={28} cy={28} r={22}
                      fill="none" stroke="var(--border-subtle)" strokeWidth={4}
                    />
                    <circle
                      cx={28} cy={28} r={22}
                      fill="none" stroke={color} strokeWidth={4}
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 22}
                      strokeDashoffset={2 * Math.PI * 22 * (1 - v.greenlight_score / 100)}
                    />
                  </svg>
                  <span
                    className="absolute inset-0 flex items-center justify-center text-sm font-bold"
                    style={{ color }}
                  >
                    {v.greenlight_score}
                  </span>
                </div>
              </div>

              {/* Badge */}
              {v.badge && (
                <div
                  className={`
                    text-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase
                    tracking-wider border ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}
                  `}
                >
                  {v.badge.label}
                </div>
              )}

              {/* Latest indicator */}
              {isLatest && (
                <p className="text-[9px] text-amber-400 font-semibold uppercase tracking-wider text-center mt-2">
                  Latest
                </p>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Single-version message                                             */
/* ------------------------------------------------------------------ */

function SingleVersionMessage() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-xl border p-6 text-center"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <History className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
      <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
        First Analysis Complete
      </h4>
      <p className="text-xs text-[var(--text-muted)] max-w-md mx-auto leading-relaxed">
        This is your script's first Greenlight analysis. After making revisions, run another
        analysis to track how your scores evolve across drafts. The evolution tracker will
        compare every version side by side.
      </p>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function ScriptEvolution({ history, currentAnalysis }) {
  const timeline = useMemo(
    () => buildTimeline(history, currentAnalysis),
    [history, currentAnalysis]
  );

  // Visibility state for sub-score lines
  const [lineVis, setLineVis] = useState({});
  const visibleLines = useMemo(() => {
    const proxy = { ...lineVis };
    proxy._toggle = (key) => setLineVis((prev) => ({ ...prev, [key]: !prev[key] }));
    return proxy;
  }, [lineVis]);

  const hasSingleVersion = timeline.length <= 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-amber-400" />
          <h3 className="text-base font-bold text-[var(--text-primary)]">
            Script Evolution
          </h3>
        </div>
        <span className="text-xs text-[var(--text-muted)] tabular-nums">
          {timeline.length} version{timeline.length !== 1 ? 's' : ''}
        </span>
      </div>

      {hasSingleVersion ? (
        <>
          {timeline.length === 1 && <VersionCards timeline={timeline} />}
          <SingleVersionMessage />
        </>
      ) : (
        <>
          {/* Section 1: Score Progression Chart */}
          <ScoreChart timeline={timeline} visibleLines={visibleLines} />

          {/* Section 2: Delta Table (two most recent) */}
          <DeltaTable
            prev={timeline[timeline.length - 2]}
            curr={timeline[timeline.length - 1]}
          />

          {/* Section 3: Version Cards */}
          <VersionCards timeline={timeline} />
        </>
      )}
    </motion.div>
  );
}
